import {
  Promotion,
  TableCheckout,
  Order,
  PromotionRequirement,
} from "@lemmonhq/types";
import { daysOfWeek as weekdays } from "../constants";

export type TimeContext = {
  currentDay: string;
  currentTime: string;
};

export function getCurrentTimeContext(): TimeContext {
  const now = new Date();
  // todo: should be from restaurantprofile
  const currentTime = now.toLocaleString("en-GB", {
    timeZone: "Europe/Zurich",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  console.log("currentTime", currentTime);
  return {
    currentDay: weekdays[now.getDay()],
    currentTime: currentTime, // HH:MM format
  };
}

/**
 * Get time context from a Date object
 */
function getTimeContextFromDate(date: Date): TimeContext {
  const currentTime = date.toLocaleString("en-GB", {
    timeZone: "Europe/Zurich",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  return {
    currentDay: weekdays[date.getDay()],
    currentTime: currentTime, // HH:MM format
  };
}

/**
 * Get the earliest order time from a list of orders
 */
function getEarliestOrderTime(orders: Order[]): Date | null {
  if (!orders || orders.length === 0) return null;

  const timestamps = orders
    .map((order) => new Date(order.created_at))
    .filter((date) => !isNaN(date.getTime()));

  if (timestamps.length === 0) return null;

  return new Date(Math.min(...timestamps.map((d) => d.getTime())));
}

/**
 * Get the earliest order time for orders containing specific product IDs
 */
function getEarliestOrderTimeForProducts(
  orders: Order[],
  productIds: number[],
): Date | null {
  if (!orders || orders.length === 0 || productIds.length === 0) return null;

  const relevantOrders = orders.filter((order) =>
    order.items?.some((item) => productIds.includes(item.product_id)),
  );

  return getEarliestOrderTime(relevantOrders);
}

/**
 * Get product IDs from requirements (product and product_category)
 */
function getProductIdsFromRequirements(
  requirements: PromotionRequirement[],
  getProductIdsFromCategories: (categoryIds: number[]) => number[],
): number[] {
  const productIds = new Set<number>();

  requirements.forEach((req) => {
    if (req.type === "product") {
      req.product_ids.forEach((id) => productIds.add(id));
    } else if (req.type === "product_category") {
      const ids = getProductIdsFromCategories(req.category_ids);
      ids.forEach((id) => productIds.add(id));
    }
  });

  return Array.from(productIds);
}

/**
 * Get time context for table checkout based on checkout type and requirements
 */
export function getTimeContextForTableCheckout(
  tableCheckout: TableCheckout,
  requirements: PromotionRequirement[],
  getProductIdsFromCategories: (categoryIds: number[]) => number[],
): TimeContext {
  let referenceDate: Date | null = null;

  if (tableCheckout.type === "full" || tableCheckout.type === "split") {
    // For full and split checkouts, use earliest order time
    referenceDate = getEarliestOrderTime(tableCheckout.orders);
  } else if (tableCheckout.type === "partial") {
    // For partial checkout, get product IDs from requirements
    const productIds = getProductIdsFromRequirements(
      requirements,
      getProductIdsFromCategories,
    );

    if (productIds.length > 0) {
      // Use earliest order time from orders containing required products
      referenceDate = getEarliestOrderTimeForProducts(
        tableCheckout.orders,
        productIds,
      );
    } else {
      // No specific product requirements, use earliest order time
      referenceDate = getEarliestOrderTime(tableCheckout.orders);
    }
  }

  // If we couldn't determine a reference date, fall back to current time
  if (!referenceDate) {
    return getCurrentTimeContext();
  }

  return getTimeContextFromDate(referenceDate);
}

export function isPromotionActiveNow(
  promotion: Promotion,
  currentDay: string,
  currentTime: string,
): boolean {
  console.log("[PROMO-TIME] Checking if active now");
  console.log("[PROMO-TIME] Current day:", currentDay, "time:", currentTime);
  console.log("[PROMO-TIME] Promotion days config:", promotion.days);
  console.log(
    "[PROMO-TIME] Available days:",
    Object.keys(promotion.days || {}),
  );

  // If no days are configured, promotion is active all the time
  if (!promotion.days || Object.keys(promotion.days).length === 0) {
    console.log("[PROMO-TIME] ✅ No days configured - active all the time");
    return true;
  }

  const daySchedule = promotion.days[currentDay];
  console.log("[PROMO-TIME] Schedule for", currentDay, ":", daySchedule);

  // If no schedule for current day, promotion is not active
  if (!daySchedule || daySchedule.length === 0) {
    console.log(
      "[PROMO-TIME] ❌ No schedule for",
      currentDay,
      "- not active",
    );
    return false;
  }

  // Check if current time falls within any of the time ranges for today
  const isActive = daySchedule.some((timeRange) => {
    const inRange =
      currentTime >= timeRange.start_time && currentTime <= timeRange.end_time;
    console.log(
      `[PROMO-TIME] Checking range ${timeRange.start_time}-${timeRange.end_time}:`,
      inRange,
    );
    return inRange;
  });

  console.log("[PROMO-TIME] Final result:", isActive ? "✅ ACTIVE" : "❌ INACTIVE");
  return isActive;
}

export function isPromotionEligible(
  promotion: Promotion,
  timeContext: TimeContext,
): boolean {
  console.log("[PROMO-TIME] Checking eligibility for:", promotion.name);
  console.log("[PROMO-TIME] Enabled:", promotion.enabled);

  if (!promotion.enabled) {
    console.log("[PROMO-TIME] ❌ Not enabled");
    return false;
  }

  const activeNow = isPromotionActiveNow(
    promotion,
    timeContext.currentDay,
    timeContext.currentTime,
  );

  console.log(
    "[PROMO-TIME] Is eligible:",
    activeNow ? "✅ YES" : "❌ NO",
  );
  return activeNow;
}

/**
 * Check if a timestamp (ISO string) falls within the promotion's active time periods
 */
export function isTimestampEligibleForPromotion(
  timestamp: string,
  promotion: Promotion,
): boolean {
  if (!promotion.enabled) {
    return false;
  }

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    console.warn("[PROMO-TIME] Invalid timestamp:", timestamp);
    return false;
  }

  const timeContext = getTimeContextFromDate(date);
  return isPromotionActiveNow(
    promotion,
    timeContext.currentDay,
    timeContext.currentTime,
  );
}
